import React, { useReducer, useRef, useState } from 'react';

import { getISO8601DateString } from '@aline/common';
import { IconButton, InputAdornment, Paper, Popper } from '@mui/material';
import type { GridFilterInputValueProps, GridFilterItem } from '@mui/x-data-grid-premium';
import { DateField, StaticDatePicker } from '@mui/x-date-pickers';
import { isValid, parseISO, startOfDay } from 'date-fns';
import { useClickAway } from 'react-use';
import { BackArrowIcon } from 'src/components/icons/BackArrowIcon';
import { DateFieldIcon } from 'src/components/icons/DateFieldIcon';
import { ForwardArrowIcon } from 'src/components/icons/ForwardArrowIcon';
import { makeStyles } from 'tss-react/mui';

const useStyles = makeStyles()(theme => ({
  popper: {
    zIndex: theme.zIndex.tooltip,
  },
}));

function parseFilterItemValue(value: unknown): Date | null {
  const parsed = typeof value === 'string' ? parseISO(value) : value instanceof Date ? value : null;
  return parsed && isValid(parsed) ? parsed : null;
}

// Date filter value input: a standard-variant DateField with a calendar popper.
export function DateFilterInput({ applyValue, item }: GridFilterInputValueProps): JSX.Element {
  const { classes } = useStyles();
  const anchorRef = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState<boolean>(false);
  const [node, setNode] = useState<HTMLDivElement | null>(null);
  const [value, setValue] = useState<Date | null>(() => parseFilterItemValue(item.value));
  /**
   * DateField resets in-progress section input when its controlled value
   * updates, so keep it uncontrolled and remount it only when something other
   * than this component changes `item.value`.
   */
  const [fieldKey, remountField] = useReducer(x => x + 1, 0);
  const prevItemValueRef = useRef<GridFilterItem['value']>(item.value);
  const lastEmittedValueRef = useRef<GridFilterItem['value']>(item.value);
  if (item.value !== prevItemValueRef.current) {
    const isOurRoundTrip = item.value === lastEmittedValueRef.current;
    prevItemValueRef.current = item.value;
    if (!isOurRoundTrip) {
      setValue(parseFilterItemValue(item.value));
      remountField();
    }
  }

  useClickAway(anchorRef, () => setOpen(false));

  function handleDateChange(date: Date | null): void {
    const adjustedDate = date && isValid(date) ? startOfDay(date) : null;
    const nextValue = adjustedDate ? getISO8601DateString(adjustedDate) : null;
    // Tag this as our own emission so the next render's transition check
    // doesn't treat the round-tripped item.value as external.
    lastEmittedValueRef.current = nextValue;
    applyValue({ ...item, value: nextValue });
    setValue(date);
  }

  function handleCalendarPick(date: Date | null): void {
    handleDateChange(date);
    remountField();
  }

  return (
    <>
      <DateField
        key={fieldKey}
        ref={setNode}
        defaultValue={value}
        slotProps={{
          textField: {
            slotProps: {
              input: {
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton size="small" onClick={() => setOpen(!open)}>
                      <DateFieldIcon />
                    </IconButton>
                  </InputAdornment>
                ),
              },
            },
          },
        }}
        variant="standard"
        onChange={(value, context) => {
          if (!context.validationError) handleDateChange(value);
        }}
      />
      <Popper
        anchorEl={node}
        className={classes.popper}
        modifiers={[
          {
            enabled: false,
            name: 'flip',
          },
          {
            enabled: false,
            name: 'preventOverflow',
          },
        ]}
        open={open}
        placement="bottom-start"
      >
        <Paper ref={anchorRef}>
          <StaticDatePicker
            // v9 keeps `displayStaticWrapperAs`. Mobile is the default;
            // desktop hides the toolbar AND widens the year grid
            // (yearsPerRow 3 → 4).
            displayStaticWrapperAs="desktop"
            slots={{
              actionBar: () => null,
              leftArrowIcon: BackArrowIcon,
              rightArrowIcon: ForwardArrowIcon,
            }}
            value={value}
            onAccept={() => setOpen(false)}
            onChange={handleCalendarPick}
            onClose={() => setOpen(false)}
          />
        </Paper>
      </Popper>
    </>
  );
}
